import {
  createContext,
  useContext as useReactContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { PromiseWithServerResult, Zero } from "@rocicorp/zero";
import { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { PlannedMeal } from "@corpuscomplete/core/nutrition/planned-meal";
import { mutators, queries } from "@corpuscomplete/core/zero";

export namespace MealPlanningSessionDetail {
  export type Context = {
    sessionId: string;
    userId: string;
    selectedMealId: string | null;
    setSelectedMealId: (id: string | null) => void;
  };

  export type Capabilities = {
    canManageSession: boolean;
    canEditSessionMeals: boolean;
  };

  export type SelectedMealState = {
    selectedMeal: PlannedMeal.Info | undefined;
    selectedMealId: string | null;
    setSelectedMealId: (id: string | null) => void;
  };

  export type MemberOption = {
    userId: string;
    label: string;
    isSelected: boolean;
    canToggle: boolean;
    disabledReason: string | null;
  };

  export type BlankMealIds = {
    mealId: string;
    plateIds: readonly string[];
    eventId: string;
  };

  export type BlankMealDraft = {
    input: PlannedMeal.CreateDraft | null;
    unavailableReason: string | null;
  };

  export type BlankMealDraftBuilder = {
    requiredPlateCount: number;
    build: (ids: BlankMealIds) => BlankMealDraft;
  };

  export type Mutations = {
    createMeal: (input: PlannedMeal.CreateDraft) => PromiseWithServerResult;
    addMember: (userId: string) => PromiseWithServerResult;
    removeMember: (userId: string) => PromiseWithServerResult;
    removeMeal: (mealId: string) => PromiseWithServerResult;
  };

  const DataContext = createContext<Context | null>(null);

  export function Provider({
    children,
    sessionId,
    userId,
  }: PropsWithChildren<{ sessionId: string; userId: string }>) {
    const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
    const value = useMemo(
      () => ({
        sessionId,
        userId,
        selectedMealId,
        setSelectedMealId,
      }),
      [selectedMealId, sessionId, userId],
    );
    return <DataContext value={value}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "MealPlanningSessionDetail.useContext must be used within MealPlanningSessionDetail.Provider",
      );
    }
    return context;
  }

  export function useSession(): MealPlanningSession.Info | undefined {
    const { sessionId } = useContext();
    const [row] = useQuery(queries.mealPlanningSession.byId({ id: sessionId }));
    return row ? MealPlanningSession.asInfo(row) : undefined;
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { sessionId } = useContext();
    const [, details] = useQuery(queries.mealPlanningSession.byId({ id: sessionId }));
    return { isLoading: details.type === "unknown" };
  }

  export function useCapabilities(): Capabilities {
    const { userId } = useContext();
    const session = useSession();
    const [spaces] = useQuery(queries.space.byMembership());
    const [myGrants] = useQuery(queries.accessGrant.myGrants());
    return useMemo(() => {
      if (!session) return { canManageSession: false, canEditSessionMeals: false };
      const parentSpace = spaces.find((space) => space.id === session.scopeId);
      const hasWriteAccess = parentSpace
        ? myGrants.some(
            (grant) =>
              grant.resourceType === "space" &&
              grant.resourceScope === "contents" &&
              grant.role === "editor" &&
              grant.resourceId === parentSpace.id,
          )
        : session.scopeId === userId;
      const hasSessionContentsWrite = myGrants.some(
        (grant) =>
          grant.resourceType === "meal_planning_session" &&
          grant.resourceScope === "contents" &&
          grant.role === "editor" &&
          grant.resourceId === session.id,
      );
      return {
        canManageSession: hasWriteAccess,
        canEditSessionMeals: hasWriteAccess || hasSessionContentsWrite,
      };
    }, [myGrants, session, spaces, userId]);
  }

  export function useSelectedMeal(): SelectedMealState {
    const session = useSession();
    const { selectedMealId, setSelectedMealId } = useContext();
    const selectedMeal =
      session?.plannedMeals.find((meal) => meal.id === selectedMealId) ?? session?.plannedMeals[0];

    useEffect(() => {
      if (!session) return;
      if (selectedMealId && session.plannedMeals.some((meal) => meal.id === selectedMealId)) return;
      setSelectedMealId(session.plannedMeals[0]?.id ?? null);
    }, [selectedMealId, session, setSelectedMealId]);

    return {
      selectedMeal,
      selectedMealId: selectedMeal?.id ?? null,
      setSelectedMealId,
    };
  }

  export function useMemberOptions(): MemberOption[] {
    const session = useSession();
    const capabilities = useCapabilities();
    const [spaces] = useQuery(queries.space.byMembership());
    return useMemo(() => {
      if (!session) return [];
      const parentSpace = spaces.find((space) => space.id === session.scopeId);
      const candidates = parentSpace?.group?.members ?? session.group?.members ?? [];
      const selectedIds = new Set(session.group?.members.map((member) => member.userId) ?? []);
      const isLastMember = selectedIds.size <= 1;
      const assignedUserIds = new Set(
        session.plannedMeals
          .filter((meal) => meal.status === "draft")
          .flatMap((meal) => meal.plates.map((plate) => plate.userId)),
      );
      return candidates.map((member) => {
        const isSelected = selectedIds.has(member.userId);
        const disabledReason = !capabilities.canManageSession
          ? "You do not have permission to manage this session."
          : isSelected && isLastMember
            ? "Meal planning session requires at least one member."
            : isSelected && assignedUserIds.has(member.userId)
              ? "Remove this member from meal plates before removing them from the session."
              : null;
        return {
          userId: member.userId,
          label: member.profile?.name ?? member.userId,
          isSelected,
          canToggle: disabledReason === null,
          disabledReason,
        };
      });
    }, [capabilities.canManageSession, session, spaces]);
  }

  export function useBlankMealDraftBuilder(): BlankMealDraftBuilder {
    const { userId } = useContext();
    const session = useSession();
    const memberUserIds = session?.group?.members.map((member) => member.userId) ?? [userId];
    return useMemo(
      () => ({
        requiredPlateCount: memberUserIds.length,
        build: () => {
          return {
            input: null,
            unavailableReason:
              "Blank session meal creation is disabled for schedule-based sessions.",
          };
        },
      }),
      [memberUserIds.length],
    );
  }

  export function useMutations(): Mutations {
    const z = useZero();
    const { sessionId } = useContext();
    return useMemo(
      () => ({
        createMeal: ({ scopeId: _scopeId, ...meal }) =>
          z.mutate(
            mutators.mealPlanningSession.createPlannedMealDrafts({
              mealPlanningSessionId: sessionId,
              meals: [meal],
            }),
          ),
        addMember: (userId) =>
          z.mutate(mutators.mealPlanningSession.addMember({ id: sessionId, userId })),
        removeMember: (userId) =>
          z.mutate(mutators.mealPlanningSession.removeMember({ id: sessionId, userId })),
        removeMeal: (mealId) => z.mutate(mutators.plannedMeal.deleteDraft({ id: mealId })),
      }),
      [sessionId, z],
    );
  }

  export function preload(zero: Zero, input: { id: string }): void {
    void zero.run(queries.mealPlanningSession.byId({ id: input.id }));
  }
}
